import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/structured-logger';
import type { WeeklyPlaybook, StrategicRecommendation, ContentInsights, TrendAnalysis } from '@/types/weekly-playbook';

/**
 * StrategistService
 * Your weekly social strategist - delivers performance playbooks and strategic recommendations
 */
export class StrategistService {
  private static logger = logger.child({ service: 'Strategist' });

  /**
   * Get latest weekly playbook for organization
   */
  static async getLatestPlaybook(organizationId: string): Promise<WeeklyPlaybook | null> {
    const { data, error } = await supabase
      .from('weekly_summaries')
      .select('*')
      .eq('organization_id', organizationId)
      .order('week_start', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      this.logger.error('Failed to fetch weekly playbook', error, { organizationId });
      throw error;
    }

    return data as WeeklyPlaybook | null;
  }

  /**
   * Get playbook history for organization
   */
  static async getPlaybookHistory(
    organizationId: string,
    limit: number = 12
  ): Promise<WeeklyPlaybook[]> {
    const { data, error } = await supabase
      .from('weekly_summaries')
      .select('*')
      .eq('organization_id', organizationId)
      .order('week_start', { ascending: false })
      .limit(limit);

    if (error) {
      this.logger.error('Failed to fetch playbook history', error, { organizationId });
      throw error;
    }

    return data as unknown as WeeklyPlaybook[];
  }

  /**
   * Mark playbook email as opened
   */
  static async markPlaybookOpened(playbookId: string): Promise<void> {
    const { error } = await supabase
      .from('weekly_summaries')
      .update({ 
        email_opened_at: new Date().toISOString()
      })
      .eq('id', playbookId);

    if (error) {
      this.logger.error('Failed to mark playbook opened', error, { playbookId });
      // Don't throw - this is non-critical
    }
  }
}
